import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, doc, deleteDoc, updateDoc, orderBy, query } from 'firebase/firestore';
import { db } from '@services/firebase';
import { Plus, Pencil, Trash2, Eye, EyeOff, ExternalLink } from 'lucide-react';
import toast from 'react-hot-toast';

const COLOR_DOT = {
  teal:   'bg-teal-500',
  purple: 'bg-purple-600',
  blue:   'bg-blue-600',
  navy:   'bg-[#1E3A8A]',
  orange: 'bg-orange-500',
};

export default function AdminFeatures() {
  const navigate = useNavigate();
  const [features, setFeatures] = useState([]);
  const [loading, setLoading]   = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(query(collection(db, 'features'), orderBy('order', 'asc')));
      setFeatures(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch {
      // order index may not exist yet — fallback without ordering
      const snap = await getDocs(collection(db, 'features'));
      setFeatures(snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => (a.order ?? 999) - (b.order ?? 999)));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggleVisible = async (feature) => {
    await updateDoc(doc(db, 'features', feature.id), { visible: !feature.visible });
    setFeatures(prev => prev.map(f => f.id === feature.id ? { ...f, visible: !f.visible } : f));
    toast.success(feature.visible ? 'Hidden from homepage' : 'Visible on homepage');
  };

  const handleDelete = async (feature) => {
    if (!window.confirm(`Delete "${feature.title}"?`)) return;
    await deleteDoc(doc(db, 'features', feature.id));
    setFeatures(prev => prev.filter(f => f.id !== feature.id));
    toast.success('Feature deleted');
  };

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Features</h1>
          <p className="text-sm text-gray-500 mt-1">Manage the "What We Deliver" section on the homepage</p>
        </div>
        <button
          onClick={() => navigate('/admin/features/new')}
          className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          New Feature
        </button>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1,2,3].map(i => (
            <div key={i} className="h-16 bg-gray-100 rounded-lg animate-pulse" />
          ))}
        </div>
      ) : features.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <p className="text-lg font-medium mb-2">No features yet</p>
          <p className="text-sm">Click "New Feature" to add your first feature card</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 font-semibold text-gray-600">#</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600">Icon</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600">Title</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600">Color</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600">Style</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600">CTA</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600">Visible</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {features.map(feature => (
                <tr key={feature.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 text-gray-400">{feature.order ?? '—'}</td>
                  <td className="px-4 py-3">
                    {feature.iconUrl ? (
                      <img src={feature.iconUrl} alt="" className="w-8 h-8 rounded object-cover" />
                    ) : (
                      <span className="text-xl">{feature.emoji || '⚡'}</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900 max-w-xs truncate">{feature.title}</p>
                    <p className="text-gray-400 text-xs truncate max-w-xs">{feature.description}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className="flex items-center gap-1.5">
                      <span className={`w-3 h-3 rounded-full ${COLOR_DOT[feature.accentColor] ?? 'bg-gray-400'}`} />
                      <span className="capitalize text-gray-600">{feature.accentColor || '—'}</span>
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                      feature.featured ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {feature.featured ? 'Featured' : 'Standard'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {feature.ctaLabel ? (
                      <span className="flex items-center gap-1 text-gray-600">
                        {feature.ctaLabel}
                        {feature.ctaLink && <ExternalLink className="w-3 h-3" />}
                      </span>
                    ) : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => toggleVisible(feature)} className="text-gray-400 hover:text-teal-600 transition-colors">
                      {feature.visible !== false ? (
                        <Eye className="w-4 h-4 text-teal-600" />
                      ) : (
                        <EyeOff className="w-4 h-4" />
                      )}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => navigate(`/admin/features/${feature.id}/edit`)}
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(feature)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
